import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input } from '@bruno/ui';
import type { AdminCaptchaSettingsInput } from '@bruno/shared';
import { adminGetCaptcha, adminUpdateCaptcha } from '../api/admin';

const URL_PATTERN = /^https?:\/\/[^/\s][^\s]*$/;

export function CaptchaSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-captcha'],
    queryFn: adminGetCaptcha,
  });

  const [enabled, setEnabled] = useState(false);
  const [instanceUrl, setInstanceUrl] = useState('');
  const [siteKey, setSiteKey] = useState('');
  // Empty string => keep existing secret on save. Anything non-empty
  // overwrites the at-rest ciphertext.
  const [siteSecret, setSiteSecret] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  // Hydrate form from server state when the query resolves / refetches.
  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setInstanceUrl(data.instanceUrl);
    setSiteKey(data.siteKey);
    setSiteSecret('');
  }, [data]);

  const mutation = useMutation({
    mutationFn: (body: AdminCaptchaSettingsInput) => adminUpdateCaptcha(body),
    onSuccess: () => {
      setOkMsg('Captcha settings saved.');
      setSiteSecret('');
      qc.invalidateQueries({ queryKey: ['admin-captcha'] });
    },
    onError: (err) => {
      setOkMsg(null);
      setFormError(err instanceof Error ? err.message : 'Failed to save settings');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setOkMsg(null);

    const trimmedUrl = instanceUrl.trim().replace(/\/+$/, '');
    if (enabled) {
      if (!trimmedUrl || !URL_PATTERN.test(trimmedUrl)) {
        setFormError('Instance URL must be a valid http(s) URL with no path.');
        return;
      }
      if (!siteKey.trim()) {
        setFormError('Site Key is required when captcha is enabled.');
        return;
      }
      if (!siteSecret && !data?.siteSecretConfigured) {
        setFormError('Site Secret is required the first time you enable captcha.');
        return;
      }
    }

    const body: AdminCaptchaSettingsInput = {
      enabled,
      instanceUrl: trimmedUrl || instanceUrl,
      siteKey: siteKey.trim(),
      ...(siteSecret ? { siteSecret } : {}),
    };
    mutation.mutate(body);
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold">Captcha</h1>
        <p className="text-slate-500 text-sm">
          Cap (
          <a
            className="text-indigo-600 hover:underline"
            href="https://trycap.dev"
            target="_blank"
            rel="noreferrer"
          >
            trycap.dev
          </a>
          ) self-hosted captcha protects the dashboard signin / signup and admin
          login. Captcha runs in <strong>programmatic mode</strong> — there is no
          visible widget, so legitimate users see no extra step.
        </p>
      </div>

      {isLoading ? (
        <div className="text-slate-500">Loading…</div>
      ) : error ? (
        <div className="text-sm text-red-600">
          Failed to load: {error instanceof Error ? error.message : 'unknown error'}
        </div>
      ) : (
        <Card>
          <form onSubmit={onSubmit} className="flex flex-col gap-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <div>
                <div className="text-sm font-medium text-slate-800">
                  Require captcha on signin / signup
                </div>
                <div className="text-xs text-slate-500">
                  When off, the auth endpoints accept requests without a captcha
                  token. Use the <code>CAP_DISABLE</code> env var as a break-glass
                  if you can't reach this page.
                </div>
              </div>
            </label>

            <Input
              label="Instance URL"
              value={instanceUrl}
              onChange={(e) => setInstanceUrl(e.target.value)}
              placeholder="https://cap.example.com"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="-mt-3 text-xs text-slate-500">
              Base URL of your Cap Standalone instance (without site key or
              trailing slash). The browser will hit this URL directly during
              proof-of-work, so it must be reachable from your users and have
              CORS configured for your dashboard / admin origins.
            </p>

            <Input
              label="Site Key"
              value={siteKey}
              onChange={(e) => setSiteKey(e.target.value)}
              placeholder="e.g. d9256640cb53"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="-mt-3 text-xs text-slate-500">
              Your site key from the Cap dashboard (log in at your instance URL).
            </p>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Site Secret</label>
                {data?.siteSecretConfigured ? (
                  <Badge tone="green">Configured</Badge>
                ) : (
                  <Badge tone="amber">Not configured</Badge>
                )}
              </div>
              <Input
                type="password"
                value={siteSecret}
                onChange={(e) => setSiteSecret(e.target.value)}
                placeholder={
                  data?.siteSecretConfigured
                    ? 'Leave blank to keep existing secret'
                    : 'Paste site secret from Cap dashboard'
                }
                autoComplete="new-password"
                spellCheck={false}
              />
              <p className="text-xs text-slate-500">
                Your site secret from the Cap dashboard (
                <strong>not</strong> the admin key). Stored encrypted at rest and
                never returned through this page.
              </p>
            </div>

            {formError ? <div className="text-sm text-red-600">{formError}</div> : null}
            {okMsg ? <div className="text-sm text-emerald-700">{okMsg}</div> : null}

            <div className="flex items-center justify-between border-t border-slate-200 pt-4">
              <div className="text-xs text-slate-500">
                {data?.updatedAt && data.updatedAt !== new Date(0).toISOString()
                  ? `Last updated ${new Date(data.updatedAt).toLocaleString()}`
                  : 'Never saved'}
              </div>
              <Button type="submit" loading={mutation.isPending}>
                Save settings
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
